import { router, useFocusEffect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DealRow } from '@/components/DealRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Skeleton } from '@/components/Skeleton';
import { colors, spacing } from '@/constants/theme';
import { dealsStore } from '@/stores/deals.store';

const DealsTab = observer(() => {
  // Fires on first focus (cold load via ensureLoaded) and every return to
  // the tab (throttled revalidation via refreshList) — cap numbers burn
  // down all day, so a session-old list goes stale fast.
  useFocusEffect(
    useCallback(() => {
      void dealsStore.ensureLoaded();
      void dealsStore.refreshList();
    }, []),
  );

  const onRefresh = useCallback(() => {
    void dealsStore.pullRefreshList();
  }, []);

  // The sections are the tab's whole list (they leave out MarketPryce Social
  // deals, which live on Create), so "nothing here" is counted from them.
  const sections = dealsStore.sections;
  const listed = sections.inProgress.length + sections.earnNext.length + sections.completed.length;
  const showLoading = dealsStore.loading && listed === 0;
  const showError = !!dealsStore.error && listed === 0;
  const showEmpty = !showLoading && !showError && listed === 0;

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={dealsStore.pullRefreshingList}
            onRefresh={onRefresh}
            tintColor={colors.deepGreen}
          />
        }
      >
        <View style={styles.header}>
          <Overline>Browse</Overline>
          <Heading size="5xl">Deals</Heading>
        </View>

        {showLoading && (
          <View style={styles.list} testID="deals-skeleton">
            <Skeleton height={96} radius="lg" />
            <Skeleton height={96} radius="lg" />
            <Skeleton height={96} radius="lg" />
          </View>
        )}

        {showError && (
          <ErrorState
            body={dealsStore.error ?? "Couldn't load deals."}
            onRetry={() => dealsStore.ensureLoaded()}
          />
        )}

        {showEmpty && (
          <EmptyState
            title="No deals yet"
            body="Deals roll in when brands launch campaigns. We'll surface them here as soon as they're live."
          />
        )}

        {!showLoading && !showError && listed > 0 && (
          <View style={styles.sections}>
            {sections.inProgress.length > 0 && (
              <View style={styles.section}>
                <Overline>In progress</Overline>
                <View style={styles.list}>
                  {sections.inProgress.map((deal) => (
                    <DealRow
                      key={deal.id}
                      deal={deal}
                      action={dealsStore.actionFor(deal.id)}
                      onPress={() => router.push(`/deals/${deal.slug}`)}
                      testID={`deal-row-${deal.slug}`}
                    />
                  ))}
                </View>
              </View>
            )}

            {sections.earnNext.length > 0 && (
              <View style={styles.section}>
                <Overline>Earn next</Overline>
                <View style={styles.list}>
                  {sections.earnNext.map((deal) => (
                    <DealRow
                      key={deal.id}
                      deal={deal}
                      onPress={() => router.push(`/deals/${deal.slug}`)}
                      testID={`deal-row-${deal.slug}`}
                    />
                  ))}
                </View>
              </View>
            )}

            {sections.completed.length > 0 && (
              <View style={styles.section}>
                <Overline>Completed</Overline>
                <View style={styles.list}>
                  {sections.completed.map(({ deal, action }) => (
                    <DealRow
                      key={deal.id}
                      deal={deal}
                      action={action}
                      onPress={() => router.push(`/deals/${deal.slug}`)}
                      testID={`deal-row-${deal.slug}`}
                    />
                  ))}
                </View>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
});

export default DealsTab;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: { padding: spacing.lg, gap: spacing.md },
  header: { gap: spacing.xs },
  sections: {
    gap: spacing.lg,
  },
  section: {
    gap: spacing.sm,
  },
  list: {
    gap: spacing.sm,
  },
});
